import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  DestroyRef,
  DOCUMENT,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { emitIframeMessage } from '@longnecktech/splash-commons-fe';
import { IframeMessage } from '@longnecktech/splash-commons-fe/dist/types/iframe-message';
import { GamesService } from '@services/games.service';
import { HeightObserverDirective } from '@shared/directives/height-observer.directive';
import {
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  map,
  take,
} from 'rxjs';

import { SessionService } from '@services/session.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  hostDirectives: [HeightObserverDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.scroll-enabled]': 'isScrollEnabled()',
  },
})
export class AppComponent implements OnInit {
  private breakpointObserver = inject(BreakpointObserver);
  private session = inject(SessionService);
  private gameService = inject(GamesService);
  private document = inject<Document>(DOCUMENT);
  private heightObserver = inject(HeightObserverDirective);

  user$ = this.session.user$;
  isMobile$ = this.session.isMobile$;
  theme$ = this.session.theme$;
  isScrollEnabled = toSignal(
    this.session.theme$.pipe(map((theme) => !!theme?.scrollEnabled)),
    { initialValue: false },
  );

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.updateBodyClass();
    this.initGame();
    this.initIsMobile();
    this.listenToHeightChanges();
  }

  private initGame(): void {
    this.user$
      .pipe(
        take(1),
        filter((user) => !!user),
      )
      .subscribe(() => {
        this.gameService.fetchGame().subscribe();
      });
  }

  private initIsMobile(): void {
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.session.setIsMobile(result.matches));
  }

  private updateBodyClass(): void {
    const bodyClass = 'mobile-version';
    this.isMobile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isMobile) => {
        if (isMobile) {
          this.document.body.classList.add(bodyClass);
        } else {
          this.document.body.classList.remove(bodyClass);
        }
      });
    this.theme$
      .pipe(
        filter((theme) => !!theme),
        take(1),
      )
      .subscribe((theme) => {
        this.document.body.classList.toggle('light-mode', !theme?.darkMode);
      });
  }

  private listenToHeightChanges(): void {
    // run this code only for mobile devices
    combineLatest([this.isMobile$, this.heightObserver.heightChange])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([isMobile]) => isMobile !== null && isMobile),
        map(([, height]) => height),
        distinctUntilChanged(),
        delay(300),
      )
      .subscribe((height) => {
        emitIframeMessage(IframeMessage.UpdateHeight, { heightInPx: height });
      });
  }
}
